import { useState, useEffect } from 'react';
import { DataSet } from '../../../types';
import { FilterExpressionParser } from '../../utils/FilterExpressionParser';
import * as styles from '../../styles/components/graph-builder.css';

interface FilterInputProps {
  value: string;
  onChange: (expression: string) => void;
  dataset: DataSet;
}

export function FilterInput({ value, onChange, dataset }: FilterInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const [error, setError] = useState<string | null>(null);

  // 親からの値の変更を反映
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // フィルタ式の検証
  const validateFilter = (expression: string): string | null => {
    if (!expression.trim()) {
      return null; // 空の場合はエラーなし
    }

    const parser = new FilterExpressionParser();
    const result = parser.parse(expression);

    if (!result.success) {
      return result.error || 'Invalid syntax';
    }

    // 列名の検証（データセットに存在する列名か）
    const columnNames = dataset.schema.columns.map(c => String(c.name));
    const usedColumns = extractColumnNames(result.expression!);

    for (const col of usedColumns) {
      if (!columnNames.includes(String(col))) {
        return `Column "${col}" not found in dataset`;
      }
    }

    return null;
  };

  // フィルタ式から列名を抽出
  const extractColumnNames = (expression: unknown): string[] => {
    if (typeof expression !== 'object' || expression === null) {
      return [];
    }

    const expr = expression as Record<string, unknown>;

    // CompoundCondition
    if (expr.type === 'compound' && Array.isArray(expr.conditions)) {
      return (expr.conditions as unknown[]).flatMap(extractColumnNames);
    }

    // NotCondition
    if (expr.type === 'not' && expr.condition) {
      return extractColumnNames(expr.condition);
    }

    // 列名を持つ条件 (ComparisonCondition, NullCondition, InCondition, BetweenCondition)
    if ('column' in expr && typeof expr.column === 'string') {
      return [expr.column];
    }

    return [];
  };

  // 入力変更ハンドラ
  const handleChange = (newValue: string) => {
    setLocalValue(newValue);

    // バリデーション
    const validationError = validateFilter(newValue);
    setError(validationError);

    // エラーがなければ親に通知
    if (!validationError) {
      onChange(newValue);
    }
  };

  // クリアハンドラ
  const handleClear = () => {
    setLocalValue('');
    setError(null);
    onChange('');
  };

  const hasError = error !== null;
  const hasValue = localValue.trim().length > 0;

  return (
    <div className={styles.filterInputContainer}>
      <div className={styles.filterInputWrapper}>
        <input
          type="text"
          className={`${styles.filterInput} ${hasError ? styles.filterInputError : ''}`}
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="e.g., Age > 30, Name LIKE '%Smith%'"
          data-testid="filter-expression-input"
        />
        {hasValue && (
          <button
            className={styles.filterClearButton}
            onClick={handleClear}
            type="button"
            data-testid="filter-clear-button"
          >
            Clear
          </button>
        )}
      </div>

      {hasError && (
        <div className={styles.filterError} data-testid="filter-error-message">
          ⚠ {error}
        </div>
      )}

      {!hasError && !hasValue && (
        <div className={styles.filterHelp}>
          Examples: Age &gt; 30, Name LIKE '%Smith%', Status IN ('Active', 'Pending')
        </div>
      )}
    </div>
  );
}
